import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import compact from 'lodash/compact';
import sumBy from 'lodash/sumBy';
import {
    Block,
    FlexDiv,
    Heading3,
    palette,
    TextInput,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import SelectEmployees from '../../components/manager/wizard/select-employees';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.label`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: block;
    margin-top: 8px;
    margin-bottom: 6px;
`;

const StepCard = styled(Block)`
    margin-bottom: 14px;
`;

const StepNumber = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${palette.evergreen.aspargus};
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const WarnAlert = styled.div`
    background: ${palette.pastel.orange};
    color: ${palette.vibrant.coral};
    border: 1px solid ${palette.vibrant.orange};
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 12px;
`;

const InfoAlert = styled.div`
    background: ${palette.pastel.aspargus};
    color: ${palette.evergreen.aspargus};
    border: 1px solid ${palette.evergreen.aspargus};
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 12px;
`;

function NewAwarePhishCampaign() {
    const navigate = useNavigate();
    const {
        store,
        listStore,
        userStore,
        awarePhishStore,
        commonStore,
    } = useStores();
    const { currentCompany } = store;
    const { listsWithUsers: lists, error: listError } = listStore;
    const { loadingUsers } = userStore;
    const {
        creatingCampaign,
        templates,
        sendingProfiles,
        deliveryProfiles,
    } = awarePhishStore;

    const [name, setName] = useState(() => {
        const d = new Date();
        const stamp = d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
        return `Phishing simulation — ${stamp}`;
    });
    const [templateId, setTemplateId] = useState('');
    const [sendingProfileId, setSendingProfileId] = useState('');
    const [deliveryProfileId, setDeliveryProfileId] = useState('');
    const [selectedListIds, setSelectedListIds] = useState([]);
    const [launchAt, setLaunchAt] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [addEmployeeModal, setAddEmployeeModal] = useState(false);
    const [recipientsModal, setRecipientsModal] = useState(false);

    const companyId = currentCompany && currentCompany.company_id;
    const createListDisabled =
        !currentCompany || !currentCompany.canAddUsers;

    useEffect(() => {
        if (companyId) {
            listStore.loadCompanyLists(companyId);
            awarePhishStore.loadConfig(companyId);
            awarePhishStore.loadTemplates(companyId);
            awarePhishStore.loadSendingProfiles(companyId);
            awarePhishStore.loadDeliveryProfiles(companyId);
        }
    }, [companyId, listStore]);

    useEffect(() => {
        if (!(templates || []).length) {
            if (templateId) setTemplateId('');
            return;
        }
        const stillValid = templates.some((t) => t.id === templateId);
        if (!templateId || !stillValid) {
            setTemplateId(templates[0].id);
        }
    }, [templates, templateId]);

    useEffect(() => {
        if (
            !sendingProfileId &&
            sendingProfiles &&
            sendingProfiles.length === 1
        ) {
            setSendingProfileId(sendingProfiles[0].id);
        }
    }, [sendingProfiles, sendingProfileId]);

    useEffect(() => {
        if (!(deliveryProfiles || []).length) {
            if (deliveryProfileId) setDeliveryProfileId('');
            return;
        }
        const stillValid = deliveryProfiles.some(
            (p) => p.id === deliveryProfileId
        );
        if (!deliveryProfileId || !stillValid) {
            const preferred =
                deliveryProfiles.find((p) => p.setupStatus === 'ready') ||
                deliveryProfiles[0];
            setDeliveryProfileId(preferred.id);
        }
    }, [deliveryProfiles, deliveryProfileId]);

    const selectedDeliveryProfile = useMemo(
        () =>
            (deliveryProfiles || []).find((p) => p.id === deliveryProfileId) ||
            null,
        [deliveryProfiles, deliveryProfileId]
    );

    const maxTargets =
        (awarePhishStore.config && awarePhishStore.config.maxTargets) || 500;

    const toggleUserListSelected = useCallback((listId) => {
        setSelectedListIds((prev) =>
            prev.includes(listId)
                ? prev.filter((x) => x !== listId)
                : [...prev, listId]
        );
    }, []);

    const userListsSelected = useMemo(
        () =>
            compact(
                selectedListIds.map((id) => listStore.allListsIndex[id])
            ),
        [selectedListIds, listStore.lists]
    );

    const selectedUserCount = useMemo(
        () =>
            sumBy(userListsSelected, (l) => Number(l.usersCount) || 0),
        [userListsSelected]
    );

    const campaignDraft = useMemo(
        () => ({
            selectedUserListsIds: selectedListIds,
            selectedUserCount,
            userListsSelected,
        }),
        [selectedListIds, selectedUserCount, userListsSelected]
    );

    const missingForSend = useMemo(() => {
        const missing = [];
        if (!String(name || '').trim()) missing.push('simulation name');
        if (!templateId) missing.push('email template');
        if (!sendingProfileId) missing.push('sending profile');
        if (!deliveryProfileId) missing.push('delivery profile');
        if (!selectedListIds.length || selectedUserCount <= 0) {
            missing.push('recipients');
        }
        if (selectedUserCount > maxTargets) {
            missing.push(`recipient limit (max ${maxTargets})`);
        }
        if (!confirmed) missing.push('confirmation');
        return missing;
    }, [
        name,
        templateId,
        sendingProfileId,
        deliveryProfileId,
        selectedListIds,
        selectedUserCount,
        maxTargets,
        confirmed,
    ]);

    const canSend =
        missingForSend.length === 0 && !creatingCampaign;

    const showAddEmployeeModal = () => setAddEmployeeModal(true);
    const hideAddEmployeeModal = () => setAddEmployeeModal(false);
    const showRecipientsModal = () => setRecipientsModal(true);
    const hideRecipientsModal = () => setRecipientsModal(false);

    const requestUsersForList = (listId) => {
        if (companyId) userStore.loadUsers(companyId, listId, true);
    };

    const createRecipientsList = async (list) => {
        list.users = (list.users || []).filter((x) => x.email);
        const result = await listStore.save(companyId, null, list);
        if (result) hideRecipientsModal();
        toggleUserListSelected(result);
        if (!addEmployeeModal) showAddEmployeeModal();
    };

    const send = async () => {
        if (!canSend) return;
        try {
            const c = await awarePhishStore.createCampaign(companyId, {
                name,
                templateId,
                sendingProfileId,
                deliveryProfileId,
                sourceListIds: selectedListIds.map(Number),
                launchAt: launchAt || undefined,
                confirm: true,
            });
            commonStore.success(
                `Simulation launched — ${selectedUserCount} recipient${
                    selectedUserCount === 1 ? '' : 's'
                }.`
            );
            navigate(`/aware-phish/campaigns/${c.id}`);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to create simulation'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>New AwarePHISH simulation</title>
            </Helmet>
            <HeaderSection
                heading={'New simulation'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Name, recipients and launch time — same shape as AwareGO 2.0 AwarePHISH.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <Link
                        to={'/aware-phish/campaigns'}
                        style={{ fontSize: 13 }}
                    >
                        ← Back to simulations
                    </Link>
                }
            />
            <AwarePhishSubNav active="campaigns" />

            <StepCard padding="20" column>
                <FlexDiv alignCenter gap={12}>
                    <StepNumber>1</StepNumber>
                    <Heading3>Details</Heading3>
                </FlexDiv>
                <FieldLabel>Simulation name</FieldLabel>
                <TextInput
                    fullWidth
                    placeholder={'e.g. Q4 credential harvest'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <FieldLabel>Email template</FieldLabel>
                <select
                    style={{ width: '100%', padding: 8, fontSize: 13 }}
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                >
                    <option value="">Select template…</option>
                    {(templates || []).map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
                <FieldLabel>Delivery profile</FieldLabel>
                <select
                    style={{ width: '100%', padding: 8, fontSize: 13 }}
                    value={deliveryProfileId}
                    onChange={(e) => setDeliveryProfileId(e.target.value)}
                >
                    <option value="">Select delivery profile…</option>
                    {(deliveryProfiles || []).map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                            {p.senderDomain && p.landingDomain
                                ? ` — ${p.senderDomain.hostname} → ${p.landingDomain.hostname}`
                                : ''}
                        </option>
                    ))}
                </select>
                {selectedDeliveryProfile ? (
                    <TextTiny lighter style={{ marginTop: 6, display: 'block' }}>
                        Landing: {selectedDeliveryProfile.landingUrl || '—'}
                        {selectedDeliveryProfile.setupStatus !== 'ready'
                            ? ` · ${selectedDeliveryProfile.setupStatus.replace(
                                  /_/g,
                                  ' '
                              )} — finish setup under Delivery`
                            : ''}
                    </TextTiny>
                ) : null}
                <FieldLabel>Sending profile (SMTP / From)</FieldLabel>
                <select
                    style={{ width: '100%', padding: 8, fontSize: 13 }}
                    value={sendingProfileId}
                    onChange={(e) => setSendingProfileId(e.target.value)}
                >
                    <option value="">Select sending profile…</option>
                    {(sendingProfiles || []).map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name} ({p.fromAddress})
                        </option>
                    ))}
                </select>
                {!(sendingProfiles || []).length ? (
                    <WarnAlert>
                        Create a sending profile under AwarePHISH → Sending
                        profiles before launching.
                    </WarnAlert>
                ) : null}
                <FieldLabel>Launch at (optional — empty = now)</FieldLabel>
                <TextInput
                    type="datetime-local"
                    fullWidth
                    value={launchAt}
                    onChange={(e) => setLaunchAt(e.target.value)}
                />
            </StepCard>

            <StepCard tablePadding column>
                <FlexDiv alignCenter gap={12} style={{ marginBottom: 8 }}>
                    <StepNumber>2</StepNumber>
                    <Heading3>Recipients</Heading3>
                </FlexDiv>
                <TextTiny lighter>
                    Select employee lists for this simulation.
                </TextTiny>
                <SelectEmployees
                    itemString="simulation"
                    item={campaignDraft}
                    lists={lists}
                    toggleUserListSelected={toggleUserListSelected}
                    ongoing={false}
                    addEmployeeModal={addEmployeeModal}
                    showAddEmployeeModal={showAddEmployeeModal}
                    hideAddEmployeeModal={hideAddEmployeeModal}
                    recipientsModal={recipientsModal}
                    showRecipientsModal={showRecipientsModal}
                    hideRecipientsModal={hideRecipientsModal}
                    error={listError}
                    loadingUsers={loadingUsers}
                    requestUsersForList={requestUsersForList}
                    selectedLists={selectedListIds}
                    selectedUserCount={selectedUserCount}
                    disabled={createListDisabled}
                    createRecipientsList={createRecipientsList}
                    currentCompany={currentCompany}
                />
                {selectedUserCount > maxTargets ? (
                    <WarnAlert>
                        Too many recipients ({selectedUserCount}). Maximum is{' '}
                        {maxTargets}.
                    </WarnAlert>
                ) : null}
            </StepCard>

            <StepCard padding="20" column>
                <FlexDiv alignCenter gap={12}>
                    <StepNumber>3</StepNumber>
                    <Heading3>Confirm &amp; send</Heading3>
                </FlexDiv>
                {selectedListIds.length === 0 ? (
                    <WarnAlert>
                        Select at least one employee list before sending.
                    </WarnAlert>
                ) : (
                    <InfoAlert>
                        This will send a phishing simulation to approximately{' '}
                        {selectedUserCount} recipient
                        {selectedUserCount === 1 ? '' : 's'}. Make sure this is
                        intentional.
                    </InfoAlert>
                )}
                <label
                    style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginTop: 12,
                        fontSize: 13,
                    }}
                >
                    <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                    />
                    I confirm sending this simulation to the selected
                    recipients
                </label>
                <FlexDiv gap={12} style={{ marginTop: 16 }} alignCenter>
                    <ThemedButton
                        text={
                            creatingCampaign
                                ? 'Sending…'
                                : 'Send simulation'
                        }
                        mode={'primary'}
                        onClick={send}
                        disabled={!canSend}
                        $noWrap
                    />
                    {!canSend && missingForSend.length > 0 ? (
                        <TextTiny lighter>
                            Missing: {missingForSend.join(', ')}
                        </TextTiny>
                    ) : null}
                </FlexDiv>
            </StepCard>
        </Container>
    );
}

export default observer(NewAwarePhishCampaign);
